import type { ReactNode } from "react";
import { Copy, Edit2 } from "iconsax-reactjs";
import { Trash2, X } from "lucide-react";
import {
  Badge,
  Flyin,
  FlyinBody,
  FlyinFooter,
  FlyinHeader,
  FlyinTitle,
} from "../../../shared/ui";
import { timeAgo } from "../../../shared/lib/utils";
import { formatAudienceSummary } from "../constants/notificationData";
import {
  formatTimestamp,
  getChannelLabel,
  STATUS_CLASSNAMES,
  STATUS_LABELS,
} from "./notificationColumns";
import { BroadcastNotification } from "../types";

type NotificationDetailFlyinProps = {
  open: boolean;
  item: BroadcastNotification | null;
  onOpenChange: (open: boolean) => void;
  onEditDraft: (item: BroadcastNotification) => void;
  onDeleteDraft: (item: BroadcastNotification) => void;
  onReuseTemplate: (item: BroadcastNotification) => void;
};

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="text-[12px] uppercase tracking-wide text-[#8D92BC]">{label}</p>
    <p className="mt-1 text-[14px] font-[450] text-[#2C2F44]">{value}</p>
  </div>
);

const NotificationDetailFlyin = ({
  open,
  item,
  onOpenChange,
  onEditDraft,
  onDeleteDraft,
  onReuseTemplate,
}: NotificationDetailFlyinProps) => {
  if (!item) return null;

  const isDraft = item.status === "draft";
  const isReusable = item.status === "sent" || item.status === "failed" || item.status === "in-progress";

  return (
    <Flyin open={open} onOpenChange={onOpenChange}>
      <FlyinHeader>
        <div className="min-w-0">
          <FlyinTitle className="truncate text-[22px] font-[600] text-[#161827]">
            {item.subject || "(No subject)"}
          </FlyinTitle>
          <Badge variant="soft" className={`mt-2 ${STATUS_CLASSNAMES[item.status]}`}>
            {STATUS_LABELS[item.status]}
          </Badge>
        </div>
        <button
          type="button"
          aria-label="Close panel"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={() => onOpenChange(false)}
        >
          <X size={20} />
        </button>
      </FlyinHeader>

      <FlyinBody className="space-y-6">
        <section>
          <p className="text-[12px] uppercase tracking-wide text-[#8D92BC]">Message</p>
          <p className="mt-2 whitespace-pre-wrap rounded-[14px] bg-[#F7F8FD] p-4 text-[14px] leading-relaxed text-[#2C2F44]">
            {item.message || "(No message)"}
          </p>
        </section>

        <section className="grid grid-cols-2 gap-4">
          <DetailRow label="Channel" value={getChannelLabel(item.channel)} />
          <DetailRow label="Recipients" value={item.recipientCount.toLocaleString()} />
          <DetailRow label="Audience" value={formatAudienceSummary(item.audience)} />
          <DetailRow label="Sent by" value={item.sentBy} />
          <DetailRow
            label={isDraft ? "Last updated" : "Sent"}
            value={
              <>
                {timeAgo(item.sentAt)}
                <span className="block text-[12px] font-[400] text-[#8D92BC]">
                  {formatTimestamp(item.sentAt)}
                </span>
              </>
            }
          />
        </section>

        {item.audience.targetType === "customer" && item.audience.customers.length > 0 ? (
          <section>
            <p className="text-[12px] uppercase tracking-wide text-[#8D92BC]">Customers</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {item.audience.customers.map((customer) => (
                <span
                  key={customer.id}
                  className="rounded-full bg-[#EEF1FB] px-3 py-1.5 text-[13px] font-[450] text-[#2C2F44]"
                >
                  {customer.label}
                </span>
              ))}
            </div>
          </section>
        ) : null}
      </FlyinBody>

      <FlyinFooter className="gap-10">
        {isDraft ? (
          <>
            <button
              type="button"
              className="flex items-center gap-2 text-[14px] font-[500] text-[#5B21FF]"
              onClick={() => onEditDraft(item)}
            >
              <Edit2 size={18} />
              Edit draft
            </button>
            <button
              type="button"
              className="flex items-center gap-2 text-[14px] font-[500] text-[#E24B4B]"
              onClick={() => onDeleteDraft(item)}
            >
              <Trash2 size={18} />
              Delete draft
            </button>
          </>
        ) : null}

        {isReusable ? (
          <button
            type="button"
            className="flex items-center gap-2 text-[14px] font-[500] text-[#5B21FF]"
            onClick={() => onReuseTemplate(item)}
          >
            <Copy size={18} />
            Reuse as new broadcast
          </button>
        ) : null}
      </FlyinFooter>
    </Flyin>
  );
};

export default NotificationDetailFlyin;
